import * as Yup from "yup";
import { ProfileFormValues, ProfileType } from "./types";

export const createProfileValidationSchema = (
  isOwner: boolean,
  maxRemainingPercentage?: number,
  minPercentage?: number,
) =>
  Yup.object().shape({
    profileType: Yup.string()
      .oneOf(["Individual", "Entity"] as ProfileType[])
      .required("Please select a profile type"),
    firstName: Yup.string().when("profileType", {
      is: "Individual",
      then: (schema) => schema.required("First name is required"),
      otherwise: (schema) => schema.strip(),
    }),
    middleName: Yup.string().when("profileType", {
      is: "Individual",
      then: (schema) => schema,
      otherwise: (schema) => schema.strip(),
    }),
    lastName: Yup.string().when("profileType", {
      is: "Individual",
      then: (schema) =>
        schema.test({
          name: "name-validation",
          test: function (value, context: Yup.TestContext<ProfileFormValues>) {
            const { firstName, lastName } = context.parent as ProfileFormValues;
            if (firstName && lastName) return true;
            if (!value) return false;
          },
          message: "Please enter a last name",
        }),
      otherwise: (schema) => schema.strip(),
    }),
    fullName: Yup.string().when("profileType", {
      is: "Individual",
      then: (schema) =>
        schema
          .required("Full name is required")
          .test(
            "has-space",
            "Full name must include both first and last name",
            (value: string | undefined) => {
              if (!value) return false;
              // Trim the value to remove leading/trailing spaces
              const trimmed = value.trim();
              // Check if there's at least one space between characters
              return trimmed.includes(" ");
            },
          ),
      otherwise: (schema) => schema.strip(),
    }),
    legalBusinessName: Yup.string().when("profileType", {
      is: "Entity",
      then: (schema) => schema.required("Legal business name is required"),
      otherwise: (schema) => schema.strip(),
    }),
    ownershipPercentage: Yup.number().when([], {
      is: () => isOwner,
      then: (schema) =>
        schema
          .required("Ownership percentage is required")
          .min(
            minPercentage || 0,
            `The minimum ownership percentage for identification purposes of this entity is ${minPercentage}%`,
          )
          .max(
            maxRemainingPercentage || 100,
            `Ownership percentage across all owner profiles cannot exceed ${maxRemainingPercentage}% for this entity`,
          ),
      otherwise: (schema) => schema.strip(),
    }),
  });

export const getInitialValues = (): ProfileFormValues => ({
  profileType: undefined,
  firstName: "",
  middleName: "",
  lastName: "",
  legalBusinessName: "",
  ownershipPercentage: undefined,
});
